import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface SyncStatus {
  lastSyncAt: Date | null;
  nextScheduledSync: Date | null;
  isSyncRunning: boolean;
  totalRecords: number;
  modifiedCount: number;
  withCmdbDifferences: number;
}

export interface SyncHistory {
  id: number;
  startedAt: Date;
  completedAt: Date | null;
  success: boolean;
  errorMessage: string | null;
  recordsInserted: number;
  recordsUpdated: number;
  recordsSkipped: number;
  recordsDeleted: number;
  recordsErrored: number;
  durationSeconds: number | null;
  isManualSync: boolean;
  triggeredBy: string | null;
  syncType: string | null;
}

export interface PagedSyncHistory {
  items: SyncHistory[];
  totalCount: number;
  totalPages: number;
  page: number;
  pageSize: number;
}

export interface OrionTestResult {
  success: boolean;
  appId: string;
  message: string;
  manifestJson: string | null;
  failureReason?: string | null;
  httpStatusCode?: number | null;
}

export interface OrionEnrichmentResult {
  success: boolean;
  updated: number;
  skipped: number;
  failed: number;
  durationSeconds: number;
  errorMessage?: string | null;
}

export interface CmdbSyncConfig {
  isAutoSyncEnabled: boolean;
  syncIntervalHours: number;
  batchSize: number;
  isSyncWithOrionEnabled: boolean;
  updatedAtUtc: string | null;
}

export interface SyncHistoryItem {
  id: number;
  syncHistoryId: number;
  sysId: string | null;
  appName: string;
  action: string; // "Inserted" | "Updated" | "Failed"
  errorMessage: string | null;
  processedAt: Date;
}

export interface PagedSyncHistoryItems {
  items: SyncHistoryItem[];
  totalCount: number;
  totalPages: number;
  page: number;
  pageSize: number;
}

@Injectable({
  providedIn: 'root'
})
export class CmdbSyncService {
  private apiUrl: string;
  private adminUrl: string;

  constructor(private http: HttpClient) {
    this.apiUrl = environment.apiUrl + '/cmdb/sync';
    this.adminUrl = environment.apiUrl + '/admin/cmdb-sync-settings';
  }

  /**
   * Get the current sync status
   */
  getSyncStatus(): Observable<SyncStatus> {
    return this.http.get<SyncStatus>(`${this.apiUrl}/status`);
  }

  /**
   * Get sync history (paginated)
   */
  getSyncHistory(page: number = 1, pageSize: number = 20): Observable<PagedSyncHistory> {
    return this.http.get<PagedSyncHistory>(`${this.apiUrl}/history?page=${page}&pageSize=${pageSize}`);
  }

  /**
   * Trigger a manual sync
   */
  triggerSync(): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/trigger`, {});
  }

  /**
   * Fetch and save the Orion manifest for a given AppId
   */
  updateOrionManifest(appId: string): Observable<OrionTestResult> {
    return this.http.post<OrionTestResult>(`${this.apiUrl}/orion-update`, { appId });
  }

  /**
   * Start Orion manifest enrichment for ALL tracked apps (no CMDB re-sync).
   * Runs in the background on the server and returns immediately (202). Track progress via Sync History.
   */
  runOrionEnrichment(): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.apiUrl}/orion-enrich`, {});
  }

  /**
   * Get CMDB auto-sync settings from the admin configuration
   */
  getSyncConfig(): Observable<CmdbSyncConfig> {
    return this.http.get<CmdbSyncConfig>(this.adminUrl);
  }

  /**
   * Update CMDB auto-sync settings
   */
  updateSyncConfig(config: CmdbSyncConfig): Observable<CmdbSyncConfig> {
    return this.http.put<CmdbSyncConfig>(this.adminUrl, config);
  }

  /**
   * Get a single SyncHistory record by ID
   */
  getSyncHistoryById(id: number): Observable<SyncHistory> {
    return this.http.get<SyncHistory>(`${this.apiUrl}/history/${id}`);
  }

  /**
   * Get paginated per-app log items for a sync history record
   */
  getSyncHistoryItems(
    syncId: number,
    page: number = 1,
    pageSize: number = 50,
    appName?: string,
    action?: string
  ): Observable<PagedSyncHistoryItems> {
    let url = `${this.apiUrl}/history/${syncId}/items?page=${page}&pageSize=${pageSize}`;
    if (appName) url += `&appName=${encodeURIComponent(appName)}`;
    if (action) url += `&action=${encodeURIComponent(action)}`;
    return this.http.get<PagedSyncHistoryItems>(url);
  }

}
